import React, { forwardRef, useMemo } from 'react';
import { FormControl, FormHelperText } from '@mui/material';
import ReactSelect, { components } from 'react-select';
import { translate as t } from 'hooks/useTranslations';
import useJurisdictions from 'data_sources/useJurisdictions';
import { Jurisdiction } from 'types/jurisdiction';

interface JurisdictionOption {
  value: string;
  label: string;
  jurisdiction: Jurisdiction;
}

export type JurisdictionFilterProps = {
  allowBlank?: boolean;
  id: string;
  label?: string;
  value?: (string | null)[];
  onChange: (value: (string | null)[]) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string | undefined;
  size?: string;
  showAllOption?: boolean;
};

const JurisdictionFilter = forwardRef(
  (
    {
      allowBlank = false,
      id,
      label,
      value = [],
      onChange,
      placeholder,
      disabled = false,
      required,
      error,
      helperText,
      size,
      showAllOption = false,
    }: JurisdictionFilterProps,
    ref
  ) => {
    const jurisdictionsQuery = useJurisdictions();

    const SingleValue = ({ children, ...props }: any) => (
      <components.SingleValue {...props}>
        {props.data?.value ? `Jurisdiction: ${children}` : t('dtbs.library.allJurisdictions')}
      </components.SingleValue>
    );

    const jurisdictionOptions = useMemo(() => {
      return [
        ...(allowBlank ? [{ value: '', label: placeholder ?? '-- Select a jurisdiction --' }] : []),
        ...(showAllOption ? [{ value: null, label: t('common.all') }] : []),
        ...(jurisdictionsQuery.data ?? []).map((x) => {
          return { value: x.id, label: `${x.name ?? ''} (${x.abbreviation})`, jurisdiction: x };
        }),
      ];
    }, [jurisdictionsQuery.data, allowBlank, placeholder, showAllOption, t]);

    const selectedOptions = useMemo(() => {
      return jurisdictionOptions.filter((x) => (value ?? []).includes(x.value));
    }, [value, jurisdictionOptions]);

    return (
      <FormControl
        key={id}
        fullWidth
        error={!!error}
        sx={{
          p: 0,
        }}
      >
        <ReactSelect
          name={id}
          value={selectedOptions}
          options={jurisdictionOptions}
          placeholder={placeholder ?? (showAllOption ? t('dtbs.library.filters.allJurisdictions') : t('common.select'))}
          menuPortalTarget={document.body}
          components={{ SingleValue }}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, width: 300 }),
            input: (base) => ({
              ...base,
              boxShadow: 'none',
              outline: 0,
              minHeight: size === 'small' ? 26 : 45,
            }),
          }}
          menuPosition='fixed'
          menuPlacement='auto'
          isSearchable
          isDisabled={disabled}
          onChange={(newValue) => {
            if (Array.isArray(newValue)) {
              onChange((newValue ?? []).map((x) => x.value));
            } else {
              onChange(!!newValue ? [newValue.value] : []);
            }
          }}
        />
        {!!error && <FormHelperText error>{error.message}</FormHelperText>}
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

export default JurisdictionFilter;
